import React, { useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { NavLink } from "../NavLink";
import type { NavLinkType } from "../NavLink/NavLink";
import { Button } from "../Button";
import type { DockFlyoutListItem } from "../../types/dockFlyoutDataTypes";

interface DockFlyoutSecondaryPanelProps {
  item: DockFlyoutListItem;
  openSecondaryGroupId: string | null;
  setOpenSecondaryGroupId: (groupId: string | null) => void;
  positionStyle: React.CSSProperties;
  onNavigate: (item: DockFlyoutListItem) => void;
  onLockOpen: () => void;
  onReload?: () => void | Promise<void>;
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave: (event: React.MouseEvent<HTMLDivElement>) => void;
}

export const DockFlyoutSecondaryPanel = React.forwardRef<
  HTMLDivElement,
  DockFlyoutSecondaryPanelProps
>(function DockFlyoutSecondaryPanel(
  {
    item,
    openSecondaryGroupId,
    setOpenSecondaryGroupId,
    positionStyle,
    onNavigate,
    onLockOpen,
    onReload,
    onMouseEnter,
    onMouseLeave,
  },
  ref,
) {
  const secondaryGroups = item.secondaryGroups ?? [];
  const formatGroupLabel = (label: string): string =>
    label.replace(/([a-z])([A-Z])/g, "$1 $2");
  const [pinOverrides, setPinOverrides] = React.useState<
    Record<string, boolean>
  >({});
  const [emptyGroupOpenByItem, setEmptyGroupOpenByItem] = React.useState<
    Record<string, boolean>
  >({});
  const [isReloading, setIsReloading] = React.useState(false);
  const isReloadingRef = React.useRef(false);
  const isEmptyGroupOpen = emptyGroupOpenByItem[item.id] !== false;
  const linkObjective = secondaryGroups
    .flatMap((group) => group.items)
    .find((child) => !!child.externalUrl);

  const handleSubstantiveClick = () => {
    if (!linkObjective?.externalUrl) {
      return;
    }

    const externalWindow = window.open(linkObjective.externalUrl, "_blank", "noopener,noreferrer");
    if (externalWindow) {
      externalWindow.opener = null;
    }
  };

  const handleReload = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (!onReload || isReloadingRef.current) {
      return;
    }

    onLockOpen();
    isReloadingRef.current = true;
    setIsReloading(true);

    try {
      await onReload();
    } catch (error) {
      console.warn("[DockFlyoutSecondaryPanel] Failed to reload workspace objectives", error);
    } finally {
      isReloadingRef.current = false;
      setIsReloading(false);
    }
  };

  const renderReloadButton = (groupLabel: string) => onReload && (
    <button
      type="button"
      className={`dock-secondary__reload-btn${isReloading ? " dock-secondary__reload-btn--loading" : ""}`}
      title={isReloading ? "Syncing..." : "Reload"}
      aria-label={isReloading ? `Syncing ${groupLabel}` : `Reload ${groupLabel}`}
      aria-busy={isReloading}
      disabled={isReloading}
      onClick={handleReload}
    >
      <span className="material-symbols-outlined" aria-hidden="true">
        refresh
      </span>
    </button>
  );

  const isPinned = (child: DockFlyoutListItem): boolean => {
    const override = pinOverrides[child.id];

    if (typeof override === "boolean") {
      return override;
    }

    return Boolean(child.pinned);
  };

  const togglePin = (
    child: DockFlyoutListItem,
    event?: React.MouseEvent<HTMLElement>,
  ) => {
    event?.stopPropagation();
    event?.preventDefault();

    setPinOverrides((prev) => ({
      ...prev,
      [child.id]: !isPinned(child),
    }));

    onLockOpen();
  };

  const toSecondaryNavItems = (
    items: DockFlyoutListItem[],
  ): NavLinkType[] => {
    return items.map((child) => ({
      key: child.id,
      label: child.name,
      leaf: true,
      data: {
        action: {
          type: "Tertiary",
          size: "Small",
          icon: "Push Pin",
          filled: false,
          onClick: () => togglePin(child),
        },
      },
    }));
  };

  const groupLookup = useMemo(() => {
    return secondaryGroups.reduce<Record<string, DockFlyoutListItem[]>>(
      (acc, group) => {
        acc[group.id] = group.items;
        return acc;
      },
      {},
    );
  }, [secondaryGroups]);

  const renderPinnedRow = (child: DockFlyoutListItem) => {
    const pinned = isPinned(child);

    return (
      <div
        key={child.id}
        role="button"
        tabIndex={0}
        className="dock-secondary__item dock-secondary__item--pinned"
        data-dock-secondary-item-id={child.id}
        onClick={(event) => {
          event.stopPropagation();
          onNavigate(child);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
            onNavigate(child);
          }
        }}
      >
        <span className="dock-secondary__item-title">{child.name}</span>

        <Button
          buttonPrefix
          buttonType="Tertiary"
          classType="Standard"
          prefixInstance="Push Pin"
          size="Small"
          state="Default"
          theme="Rectangular"
          aria-label={pinned ? "Unpin" : "Pin"}
          title={pinned ? "Unpin" : "Pin"}
          buttonClass={`dock-secondary__pin-btn${pinned ? " dock-secondary__pin-btn--pinned" : ""
            }`}
          onClick={(event) => togglePin(child, event)}
        />
      </div>
    );
  };

  return (
    <div
      ref={ref}
      className="dock-secondary"
      data-dock-secondary-panel="true"
      role="dialog"
      aria-label={`${item.name} options`}
      style={positionStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {secondaryGroups.length > 0 ? (
        secondaryGroups.map((group) => {
          const isOpen = group.id === openSecondaryGroupId;
          const nonLinkChildren = group.items.filter(
            (child) => !child.externalUrl,
          );
          const pinnedChildren = nonLinkChildren.filter((child) =>
            isPinned(child),
          );
          const nonPinnedChildren = nonLinkChildren.filter(
            (child) => !isPinned(child),
          );
          const navItems = toSecondaryNavItems(nonPinnedChildren);
          const groupLabel = formatGroupLabel(group.label);

          const toggleGroup = () => {
            onLockOpen();
            setOpenSecondaryGroupId(isOpen ? null : group.id);
          };

          return (
            <section
              key={group.id}
              className={`dock-secondary__group${isOpen
                ? " dock-secondary__group--open"
                : " dock-secondary__group--collapsed"
                }`}
              data-dock-secondary-group-id={group.id}
            >
              <div
                className="dock-secondary__group-header"
                data-expanded={isOpen ? "true" : "false"}
              >
                <button
                  type="button"
                  className="dock-secondary__group-title-button"
                  aria-expanded={isOpen}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleGroup();
                  }}
                >
                  <span className="dock-secondary__group-title">
                    {groupLabel}
                  </span>
                </button>

                {renderReloadButton(groupLabel)}

                <button
                  type="button"
                  className="dock-secondary__group-chevron-btn"
                  aria-label={`${isOpen ? "Collapse" : "Expand"} ${groupLabel}`}
                  aria-expanded={isOpen}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleGroup();
                  }}
                >
                  <ChevronDown
                    size={18}
                    className="dock-secondary__group-chevron"
                  />
                </button>
              </div>

              {pinnedChildren.length > 0 && (
                <div className="dock-secondary__group-pinned">
                  {pinnedChildren.map(renderPinnedRow)}
                </div>
              )}

              {isOpen && nonPinnedChildren.length > 0 && (
                <div className="dock-secondary__group-body">
                  <NavLink
                    items={navItems}
                    showIcon={false}
                    showNotification={false}
                    treeProps={{
                      selectionMode: "single",
                      onSelect: (event) => {
                        const groupItems = groupLookup[group.id] ?? [];
                        const selectedItem = groupItems.find(
                          (child) =>
                            child.id === String(event.node.key),
                        );

                        if (!selectedItem) {
                          return;
                        }

                        onNavigate(selectedItem);
                      },
                    }}
                  />
                </div>
              )}
            </section>
          );
        })
      ) : (
        <section
          className={`dock-secondary__group${isEmptyGroupOpen
            ? " dock-secondary__group--open"
            : " dock-secondary__group--collapsed"
            }`}
          data-dock-secondary-group-id="process-planner"
        >
          <div
            className="dock-secondary__group-header"
            data-expanded={isEmptyGroupOpen ? "true" : "false"}
          >
            <button
              type="button"
              className="dock-secondary__group-title-button"
              aria-expanded={isEmptyGroupOpen}
              onClick={(event) => {
                event.stopPropagation();
                onLockOpen();
                setEmptyGroupOpenByItem((prev) => ({
                  ...prev,
                  [item.id]: !isEmptyGroupOpen,
                }));
              }}
            >
              <span className="dock-secondary__group-title">
                Process Planner
              </span>
            </button>

            {renderReloadButton("Process Planner")}

            <button
              type="button"
              className="dock-secondary__group-chevron-btn"
              aria-label={`${isEmptyGroupOpen ? "Collapse" : "Expand"} Process Planner`}
              aria-expanded={isEmptyGroupOpen}
              onClick={(event) => {
                event.stopPropagation();
                onLockOpen();
                setEmptyGroupOpenByItem((prev) => ({
                  ...prev,
                  [item.id]: !isEmptyGroupOpen,
                }));
              }}
            >
              <ChevronDown
                size={18}
                className="dock-secondary__group-chevron"
              />
            </button>
          </div>

          {isEmptyGroupOpen && (
            <div className="dock-secondary__empty">
              No supported business processes were identified as relevant in the 2.1.5 Business Processes screen of the linked KCW engagement.
            </div>
          )}
        </section>
      )}

      {linkObjective && (
        <button
          type="button"
          className="dock-secondary__substantive-item"
          aria-label={`Open ${linkObjective.name}`}
          onClick={(event) => {
            event.stopPropagation();
            handleSubstantiveClick();
          }}
        >
          <span className="dock-secondary__item-title">{linkObjective.name}</span>
          <span className="material-symbols-outlined ws-tile-flyout__sp-btn-icon" aria-hidden="true">launch</span>
        </button>
      )}
    </div>
  );
});